// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';

const categories = [
  {
    id: 'vigilanza',
    label: 'Autorità di Vigilanza',
    color: '#5B8DB8',
    links: [
      { name: 'CONSOB', url: 'https://www.consob.it', desc: 'Commissione Nazionale per le Società e la Borsa — supervisione mercati finanziari italiani' },
      { name: 'Banca d\'Italia', url: 'https://www.bancaditalia.it', desc: 'Banca centrale italiana — supervisione bancaria, politica monetaria, stabilità finanziaria' },
      { name: 'IVASS', url: 'https://www.ivass.it', desc: 'Istituto per la Vigilanza sulle Assicurazioni — supervisione del settore assicurativo' },
      { name: 'Arbitro Bancario Finanziario (ABF)', url: 'https://www.arbitrobancariofinanziario.it', desc: 'Risoluzione stragiudiziale delle controversie con banche e intermediari finanziari' },
    ],
  },
  {
    id: 'fisco',
    label: 'Fisco e Previdenza',
    color: '#C9A84C',
    links: [
      { name: 'Agenzia delle Entrate', url: 'https://www.agenziaentrate.gov.it', desc: 'Dichiarazioni, rimborsi, F24, visure catastali e servizi fiscali' },
      { name: 'INPS', url: 'https://www.inps.it', desc: 'Pensioni, NASpI, maternità, assegno unico, estratto contributivo (myINPS)' },
      { name: 'INAIL', url: 'https://www.inail.it', desc: 'Assicurazione infortuni sul lavoro e malattie professionali' },
      { name: 'Ministero dell\'Economia (MEF)', url: 'https://www.mef.gov.it', desc: 'Bilancio dello Stato, BTP, debito pubblico, normativa fiscale' },
    ],
  },
  {
    id: 'investimenti',
    label: 'Investimenti e Mercati',
    color: '#3D7A52',
    links: [
      { name: 'Borsa Italiana', url: 'https://www.borsaitaliana.it', desc: 'Mercati azionari, ETF, obbligazioni quotate in Italia (Gruppo Euronext)' },
      { name: 'Mediobanca Securities', url: 'https://www.mediobanca.com', desc: 'Ricerche e rating su titoli italiani' },
      { name: 'ESMA', url: 'https://www.esma.europa.eu', desc: 'Autorità europea dei valori mobiliari e dei mercati — vigilanza a livello UE' },
      { name: 'BancaIFIS Mercati', url: 'https://www.bancaifis.it', desc: 'Tassi e dati sui conti deposito italiani' },
    ],
  },
  {
    id: 'educazione',
    label: 'Educazione Finanziaria',
    color: '#9B7DD4',
    links: [
      { name: 'Quello che i Soldi non Dicono (CONSOB)', url: 'https://www.quellocheisoldonondicono.it', desc: 'Portale di educazione finanziaria della CONSOB per risparmiatori e investitori' },
      { name: 'Patti Chiari', url: 'https://www.pattichiari.it', desc: 'Iniziativa ABI per la trasparenza bancaria e l\'educazione finanziaria' },
      { name: 'Edufin', url: 'https://www.quellocheisoldonondicono.it', desc: 'Programma nazionale per l\'educazione finanziaria — MiPAF, MEF, MIUR' },
    ],
  },
  {
    id: 'consumer',
    label: 'Tutela del Consumatore',
    color: '#E84040',
    links: [
      { name: 'Codacons', url: 'https://www.codacons.it', desc: 'Coordinamento delle associazioni per la difesa dell\'ambiente e dei diritti degli utenti' },
      { name: 'Altroconsumo', url: 'https://www.altroconsumo.it', desc: 'Associazione indipendente per la tutela dei consumatori — test prodotti, reclami' },
      { name: 'Conciliatore BancaFinanza', url: 'https://www.conciliatorebancario.it', desc: 'Risoluzione alternativa di controversie con banche e intermediari finanziari' },
    ],
  },
];

const LinkCard = ({ link }) => (
  <a href={link.url} target="_blank" rel="noopener noreferrer"
    className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/[0.03] transition-colors group"
    data-testid={`link-${link.name.replace(/\s+/g, '-').toLowerCase()}`}>
    <ExternalLink className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#888780] group-hover:text-[#C9A84C] transition-colors" />
    <div>
      <p className="text-sm font-semibold text-[#E8E8E4] group-hover:text-[#C9A84C] transition-colors">{link.name}</p>
      <p className="text-xs text-[#888780] mt-0.5 leading-relaxed">{link.desc}</p>
    </div>
  </a>
);

const RisorseLinksPage = () => (
  <div className="max-w-4xl mx-auto px-4 py-12 sm:py-20">
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
      <a href="/risorse" className="text-xs text-[#888780] hover:text-[#E8E8E4] transition-colors mb-4 block">← Hub Risorse</a>
      <p className="text-xs text-[#C9A84C] font-semibold tracking-widest uppercase mb-3">Risorse · Riferimenti</p>
      <h1 className="text-4xl font-bold text-[#E8E8E4] mb-4" style={{ fontFamily: 'Georgia, serif' }}>Link Utili</h1>
      <p className="text-[#888780] leading-relaxed">
        Raccolta di siti istituzionali, autorità di vigilanza e strumenti di tutela del consumatore selezionati dalla redazione SAPIENS.
      </p>
    </motion.div>

    <div className="space-y-6" data-testid="link-utili-list">
      {categories.map((cat, i) => (
        <motion.div key={cat.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
          className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="px-5 py-3 flex items-center gap-2" style={{ background: `${cat.color}12`, borderBottom: `1px solid ${cat.color}30` }}>
            <div className="w-2 h-2 rounded-full" style={{ background: cat.color }} />
            <h2 className="text-sm font-bold" style={{ color: cat.color }}>{cat.label}</h2>
          </div>
          <div className="p-2">
            {cat.links.map(link => <LinkCard key={link.name} link={link} />)}
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

export default RisorseLinksPage;
